import { useState, useMemo, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getCompanies } from '../../../../utils/companyDataLoader';
import { ProductManager } from '../../../../services/productManager';
import type { CompanyState } from '../../../../types/company';
import { formatCurrency } from '../../../../utils/currencyFormatter';
import { formatCompactNumber } from '../../../../utils/numberFormatter';

ChartJS.register(ArcElement, Tooltip, Legend);

interface MarketShareSectionProps {
  companyState: CompanyState;
  products?: Array<{
    id: string;
    name: string;
    status: string;
    price?: number;
    techScore?: number;
    marketAppeal?: number;
    releaseDate?: {
      day: number;
      month: number;
      year: number;
    };
    salesMetrics?: {
      unitsSold: number;
    };
    salesHistory?: {
      totalUnits: number;
    };
  }>;
  companyName?: string;
  onNavigate?: () => void;
  showTopSelling?: boolean;
}

export function MarketShareSection({ companyState, products = [], companyName = 'Your Company', onNavigate, showTopSelling = false }: MarketShareSectionProps) {
  const [currentView, setCurrentView] = useState<'products' | 'companies' | 'topSelling'>('products');
  const [competitorProducts, setCompetitorProducts] = useState<Array<{
    id: string;
    name: string;
    status: string;
    price: number;
    techScore: number;
    marketAppeal: number;
    metadata: {
      companyId: string;
    };
    salesMetrics?: {
      unitsSold: number;
    };
  }>>([]);
  
  // Fetch competitor products from database
  useEffect(() => {
    const fetchCompetitorProducts = async () => {
      try {
        const result = await ProductManager.getAllProducts();
        if (result.success && result.data) {
          const competitors = result.data.filter(product => 
            product.metadata?.companyId && 
            product.metadata.companyId !== 'player' &&
            product.status === 'production'
          );
          setCompetitorProducts(competitors);
        }
      } catch (error) {
        console.error('Error fetching competitor products:', error);
      }
    };
    
    fetchCompetitorProducts();
  }, [companyState?.year, companyState?.month]);
  
  // Calculate market share data using same logic as MarketView
  const marketShareData = useMemo(() => {
    if (!companyState) return { products: [], companies: [], totalUnits: 0 };

    // Get competitor companies
    const companies = getCompanies({ year: companyState.year });
    
    // Process all products (player + competitors)
    const allProducts = [
      // Player products
      ...products
        .filter(p => p.status === 'production')
        .map(p => ({
          id: p.id,
          companyName: companyName,
          name: p.name,
          price: p.price || 0,
          techScore: p.techScore || 0,
          marketAppeal: p.marketAppeal || 0,
          unitsSold: p.salesMetrics?.unitsSold || p.salesHistory?.totalUnits || 0,
          isPlayerProduct: true
        })),
      // Competitor products from database
      ...competitorProducts.map(product => {
        const company = companies.find(c => c.id === product.metadata.companyId);
        return {
          id: product.id,
          companyName: company?.name || product.metadata.companyId,
          name: product.name,
          price: product.price || 0,
          techScore: product.techScore || 0,
          marketAppeal: product.marketAppeal || 0,
          unitsSold: product.salesMetrics?.unitsSold || 0,
          isPlayerProduct: false
        };
      })
    ];

    const activeProducts = allProducts.filter(p => p.unitsSold > 0);
    const totalUnits = activeProducts.reduce((sum, p) => sum + p.unitsSold, 0);
    
    // Product market share
    const productShares = activeProducts.map(p => ({
      ...p,
      marketShare: totalUnits > 0 ? (p.unitsSold / totalUnits) * 100 : 0
    }));
    
    // Company market share
    const companyShares = new Map<string, { name: string; units: number; marketShare: number; isPlayer: boolean }>();
    activeProducts.forEach(p => {
      const existing = companyShares.get(p.companyName) || { name: p.companyName, units: 0, marketShare: 0, isPlayer: p.isPlayerProduct };
      existing.units += p.unitsSold;
      existing.marketShare = totalUnits > 0 ? (existing.units / totalUnits) * 100 : 0;
      companyShares.set(p.companyName, existing);
    });
    
    return {
      products: productShares.slice(0, 10), // Top 10 products
      companies: Array.from(companyShares.values()),
      totalUnits
    };
  }, [companyState, products, companyName, competitorProducts]);

  // Pie chart colors - same as MarketView
  const colors = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'
  ];

  // Product pie chart data
  const productPieData = {
    labels: marketShareData.products.map(p => `${p.name} (${p.companyName})`),
    datasets: [{
      data: marketShareData.products.map(p => p.marketShare),
      backgroundColor: colors,
      borderWidth: 1
    }]
  };

  // Company pie chart data - same structure as MarketView
  const companyPieData = {
    labels: marketShareData.companies.map(c => c.name),
    datasets: [{
      data: marketShareData.companies.map(c => c.marketShare),
      backgroundColor: marketShareData.companies.map((c, i) => c.isPlayer ? '#3B82F6' : colors[i % colors.length]),
      borderWidth: 1
    }]
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          maxWidth: 80,
          usePointStyle: true,
          font: {
            size: 10
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context: { label: string; parsed: number }) => {
            const value = context.parsed;
            return `${context.label}: ${value.toFixed(1)}%`;
          }
        }
      }
    }
  };

  const toggleView = () => {
    if (showTopSelling) {
      setCurrentView(current => {
        if (current === 'companies') return 'products';
        if (current === 'products') return 'topSelling';
        return 'companies';
      });
    } else {
      setCurrentView(current => current === 'products' ? 'companies' : 'products');
    }
  };

  const getCurrentData = () => {
    return currentView === 'products' ? productPieData : companyPieData;
  };

  const getCurrentTitle = () => {
    if (currentView === 'products') return 'Market Share by Product';
    if (currentView === 'companies') return 'Market Share by Company';
    return 'Top Selling Products';
  };

  const hasData = () => {
    if (currentView === 'topSelling') return topProducts.length > 0;
    // Only show market share data if there are actual sales
    const hasActualSales = marketShareData.totalUnits > 0;
    return currentView === 'products' 
      ? marketShareData.products.length > 0 && hasActualSales
      : marketShareData.companies.length > 0 && hasActualSales;
  };

  // Top selling products data from real market data
  const topProducts = useMemo(() => {
    return marketShareData.products
      .sort((a, b) => b.unitsSold - a.unitsSold) // Sort by units sold descending
      .slice(0, 5) // Take top 5
      .map(product => ({
        name: product.name,
        company: product.companyName,
        price: formatCurrency(product.price),
        unitsSold: product.unitsSold,
        marketShare: product.marketShare
      }));
  }, [marketShareData.products]);

  if (!companyState) {
    return (
      <div className="border border-theme-border bg-theme-bg-content/50 rounded-lg relative overflow-hidden h-full">
        <div className="absolute top-0 left-0 right-0 z-10 bg-theme-bg-content p-2 text-center font-bold text-sm text-theme-text-secondary border-b border-theme-border">
          Market Share by Product
        </div>
        <div className="pt-10 p-3 h-full text-center text-theme-text-muted">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="border border-theme-border bg-theme-bg-content/50 rounded-lg relative overflow-hidden h-full">
      <div className="absolute top-0 left-0 right-0 z-10 bg-theme-bg-content p-2 border-b border-theme-border">
        <div className="flex items-center justify-between">
          <button
            onClick={toggleView}
            className="p-1 hover:bg-theme-bg-tertiary rounded transition-colors"
            title="Previous view"
          >
            <ChevronLeft className="w-3 h-3 text-theme-text-secondary" />
          </button>

          <button
            onClick={onNavigate}
            className={`font-bold text-xs text-theme-text-secondary text-center flex-1 ${
              onNavigate ? 'hover:text-theme-info transition-colors cursor-pointer' : ''
            }`}
            disabled={!onNavigate}
          >
            {getCurrentTitle()}
          </button>

          <button
            onClick={toggleView}
            className="p-1 hover:bg-theme-bg-tertiary rounded transition-colors"
            title="Next view"
          >
            <ChevronRight className="w-3 h-3 text-theme-text-secondary" />
          </button>
        </div>
      </div>

      <div className="pt-10 p-3 h-full">
        {currentView === 'topSelling' ? (
          // Top Selling Products View
          <div className="h-full flex flex-col">
            {topProducts.length > 0 ? (
              <div className="space-y-2 overflow-y-auto max-h-full">
                {topProducts.map((product, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-theme-border last:border-b-0">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-xs truncate">{product.name}</div>
                      <div className="text-xs text-theme-text-secondary truncate">{product.company}</div>
                      <div className="text-xs text-theme-info">{formatCompactNumber(product.unitsSold)} units</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-theme-success text-xs">{product.price}</div>
                      <div className="text-xs text-theme-text-muted">{(product.marketShare * 100).toFixed(1)}%</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex-1 min-h-0">
                <div className="flex items-center justify-center h-full text-theme-text-muted text-xs">
                  No products with sales data
                </div>
              </div>
            )}
          </div>
        ) : (
          // Market Share Chart Views
          <div className="h-full flex flex-col">
            <div className="flex-1 min-h-0">
              {hasData() ? (
                <Pie data={getCurrentData()} options={pieOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-theme-text-muted text-xs">
                  {marketShareData.totalUnits === 0
                    ? 'No products have been sold yet'
                    : currentView === 'products' 
                      ? 'No products with sales data'
                      : 'No companies with sales data'
                  }
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}